import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Award, Briefcase, Building2, CheckCircle2, ChevronDown, ClipboardList, Code2,
  Coins, FileText, GraduationCap, Headphones, Heart, Loader2, MapPin, Megaphone, Package,
  Rocket, Search, Send, TrendingUp, Upload, Users, Warehouse, X,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import { PlayerModal } from "./MediaHub";
import api, { apiError, API_BASE } from "../lib/api";

const LOCATIONS = ["Hyderabad", "Bengaluru", "Chennai", "Mumbai", "Delhi", "Pune", "Other"];
const DEPARTMENTS = ["Operations", "Delivery", "Customer Support", "Sales", "Business Development",
  "Finance", "HR", "Technology", "Marketing", "Warehouse", "Management", "Other"];
const EMP_TYPES = ["Full Time", "Part Time", "Contract", "Internship", "Field", "Remote", "Hybrid"];
const EXP_LEVELS = ["Fresher", "0–1 Years", "1–3 Years", "3–5 Years", "5+ Years"];
const POSTED = [{ k: "", l: "Anytime" }, { k: "1", l: "Today" }, { k: "7", l: "Last 7 Days" }, { k: "30", l: "Last 30 Days" }];

const input = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-slate-400 focus:border-saffron focus:ring-2 focus:ring-saffron/20";
const label = "mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-500";

const DEPT_CARDS = [
  { icon: Package, name: "Operations", desc: "Logistics and shipment operations." },
  { icon: Headphones, name: "Customer Support", desc: "Helping customers with their shipping journey." },
  { icon: TrendingUp, name: "Sales", desc: "Building customer and business relationships." },
  { icon: Code2, name: "Technology", desc: "Building digital logistics experiences." },
  { icon: Coins, name: "Finance", desc: "Managing financial operations." },
  { icon: Users, name: "HR", desc: "Building teams and employee experiences." },
  { icon: Megaphone, name: "Marketing", desc: "Growing the IndiaFast brand." },
  { icon: Warehouse, name: "Warehouse", desc: "Managing shipment processing and movement." },
];

const WHY = [
  { icon: Rocket, title: "Growth Opportunities", desc: "Build skills and take on new responsibilities." },
  { icon: GraduationCap, title: "Learning", desc: "Learn logistics, operations, technology and customer service." },
  { icon: Users, title: "Team Culture", desc: "Work with people across the IndiaFast network." },
  { icon: Code2, title: "Technology", desc: "Be part of technology-enabled logistics operations." },
  { icon: Briefcase, title: "Career Diversity", desc: "Opportunities across multiple functions." },
  { icon: Heart, title: "Make an Impact", desc: "Help connect people and businesses across India and the world." },
];

const PATH = ["Trainee", "Executive", "Senior Executive", "Team Leader", "Manager", "Senior Manager", "Leadership"];
const VALUE_ICONS = { 0: Heart, 1: Award, 2: Package, 3: Headphones };

const mediaImg = (path, id) => (path ? `${API_BASE}/api/files/${path}` : id ? `${API_BASE}/api/mediahub/public/file/${id}/card` : null);

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [cms, setCms] = useState(null);
  const [lifeMedia, setLifeMedia] = useState([]);
  const [teamVideos, setTeamVideos] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState({ q: "", location: "", department: "" });
  const [filters, setFilters] = useState({ type: "", exp: "", posted: "" });
  const [openJob, setOpenJob] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const [talentOpen, setTalentOpen] = useState(false);
  const [success, setSuccess] = useState(null);
  const [player, setPlayer] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta(
    "Careers at IndiaFast | Jobs in Logistics & Courier Operations",
    "Build your career with IndiaFast Global Couriers. Explore jobs across operations, delivery, customer support, technology and more — and grow with a technology-driven logistics network.",
    "/p/careers"
  );

  useEffect(() => {
    Promise.all([
      api.get("/careers/public").then((r) => { setJobs(r.data.jobs); setCms(r.data.cms); }).catch(() => {}),
      api.get("/mediahub/public?category=Team%20IndiaFast&limit=12").then((r) => setTeamVideos(r.data.items.filter((m) => m.media_type === "video" || m.media_type === "reel"))).catch(() => {}),
      api.get("/mediahub/public?category=Behind%20the%20Scenes&limit=12").then((r) => setLifeMedia((p) => [...p, ...r.data.items])).catch(() => {}),
      api.get("/mediahub/public?category=Events&limit=8").then((r) => setLifeMedia((p) => [...p, ...r.data.items])).catch(() => {}),
      api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // JobPosting JSON-LD for published jobs
  useEffect(() => {
    if (!jobs.length) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "careers-jobs-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": jobs.map((j) => ({
        "@type": "JobPosting", title: j.title, description: j.description || j.title,
        identifier: { "@type": "PropertyValue", name: "IndiaFast Global Couriers", value: j.job_id },
        datePosted: (j.created_at || "").slice(0, 10),
        ...(j.deadline ? { validThrough: j.deadline } : {}),
        employmentType: j.employment_type,
        hiringOrganization: { "@type": "Organization", name: "IndiaFast Global Couriers", sameAs: "https://www.indiafast.in" },
        jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: j.location, addressCountry: "IN" } },
      })),
    });
    document.head.appendChild(ld);
    return () => document.getElementById("careers-jobs-jsonld")?.remove();
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = search.q.toLowerCase();
    const now = Date.now();
    return jobs.filter((j) => {
      if (q && ![j.title, j.department, j.location, ...(j.skills || [])].join(" ").toLowerCase().includes(q)) return false;
      if (search.location && !j.location.toLowerCase().includes(search.location.toLowerCase()) && search.location !== "Other") return false;
      if (search.location === "Other" && LOCATIONS.slice(0, 6).some((l) => j.location.toLowerCase().includes(l.toLowerCase()))) return false;
      if (search.department && j.department !== search.department) return false;
      if (filters.type && j.employment_type !== filters.type) return false;
      if (filters.exp && (j.experience || "") !== filters.exp && !(filters.exp === "Fresher" && /fresher|0/i.test(j.experience || ""))) return false;
      if (filters.posted) {
        const days = (now - new Date(j.created_at).getTime()) / 86400000;
        if (days > parseInt(filters.posted)) return false;
      }
      return true;
    });
  }, [jobs, search, filters]);

  const heroImg = cms?.hero_image_path ? `${API_BASE}/api/files/${cms.hero_image_path}` : null;
  const stats = (cms?.stats || []).filter((s) => s.value);
  const stories = cms?.employee_stories || [];
  const values = cms?.values || [];
  const faqs = cms?.faqs || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="careers-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="careers-hero">
        {heroImg && <img src={heroImg} alt="IndiaFast team" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/85 to-navy" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-20" aria-hidden="true">
          <line x1="10%" y1="70%" x2="35%" y2="35%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="35%" y1="35%" x2="65%" y2="60%" stroke="#ffffff" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="65%" y1="60%" x2="90%" y2="30%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <circle cx="10%" cy="70%" r="4" fill="#f97316" className="network-pulse" />
          <circle cx="65%" cy="60%" r="4" fill="#ffffff" className="network-pulse" style={{ animationDelay: "0.9s" }} />
          <circle cx="90%" cy="30%" r="4" fill="#16a34a" className="network-pulse" style={{ animationDelay: "1.6s" }} />
        </svg>
        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">Careers at IndiaFast</p>
          <h1 className="animate-fade-up stagger-1 mx-auto mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Build Your Future With <span className="text-saffron">IndiaFast</span>
          </h1>
          <p className="animate-fade-up stagger-2 mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Join a growing logistics network where people, technology and opportunity move together.
          </p>
          <div className="animate-fade-up stagger-3 mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="#careers-jobs" data-testid="careers-hero-explore"
               className="rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">Explore Jobs</a>
            <button onClick={() => setTalentOpen(true)} data-testid="careers-hero-upload"
                    className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              <Upload className="h-4 w-4" /> Upload Resume
            </button>
          </div>
        </div>
      </section>

      {/* JOB SEARCH BAR */}
      <div className="relative z-10 mx-auto -mt-10 max-w-5xl px-4 sm:px-6" data-testid="careers-searchbar">
        <form onSubmit={(e) => e.preventDefault()}
              className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl sm:grid-cols-[2fr_1fr_1fr_auto]">
          <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input className="w-full bg-transparent py-3 text-sm text-navy outline-none placeholder:text-slate-400"
                   data-testid="careers-search-q" placeholder="Job title, keyword or skill"
                   value={search.q} onChange={(e) => setSearch({ ...search, q: e.target.value })} />
          </div>
          <select className={`${input} border-0 bg-slate-50`} data-testid="careers-search-location" value={search.location} onChange={(e) => setSearch({ ...search, location: e.target.value })}>
            <option value="">All Locations</option>
            {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select className={`${input} border-0 bg-slate-50`} data-testid="careers-search-dept" value={search.department} onChange={(e) => setSearch({ ...search, department: e.target.value })}>
            <option value="">All Departments</option>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button type="submit" data-testid="careers-search-btn"
                  className="rounded-xl bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">
            SEARCH JOBS
          </button>
        </form>
      </div>

      {/* OPEN POSITIONS */}
      <section id="careers-jobs" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="careers-jobs-section">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Open Positions</p>
        <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">Find Your Next Opportunity</h2>
        <p className="mt-2 text-sm text-slate-500">Explore current openings across the IndiaFast network.</p>

        {/* filters */}
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="careers-filters">
          <select className={`${input} max-w-44`} data-testid="careers-filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Employment Type</option>{EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className={`${input} max-w-40`} data-testid="careers-filter-exp" value={filters.exp} onChange={(e) => setFilters({ ...filters, exp: e.target.value })}>
            <option value="">Experience</option>{EXP_LEVELS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className={`${input} max-w-40`} data-testid="careers-filter-posted" value={filters.posted} onChange={(e) => setFilters({ ...filters, posted: e.target.value })}>
            {POSTED.map((p) => <option key={p.k} value={p.k}>{p.l}</option>)}
          </select>
          {(filters.type || filters.exp || filters.posted || search.q || search.location || search.department) && (
            <button onClick={() => { setFilters({ type: "", exp: "", posted: "" }); setSearch({ q: "", location: "", department: "" }); }}
                    data-testid="careers-clear-filters" className="flex items-center gap-1 text-xs font-bold text-saffron hover:text-saffron-hover">
              <X className="h-3.5 w-3.5" /> Clear Filters
            </button>
          )}
          <span className="ml-auto text-xs font-semibold text-slate-400" data-testid="careers-results-count">{filtered.length} role{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading openings…</div>
        ) : !filtered.length ? (
          <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm" data-testid="careers-empty">
            <Briefcase className="mx-auto h-10 w-10 text-saffron/50" />
            <p className="mt-4 font-heading text-xl font-bold text-navy">No current openings</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
              We're always looking for talented people. Submit your resume and we'll keep you in mind for future opportunities.
            </p>
            <button onClick={() => setTalentOpen(true)} data-testid="careers-empty-submit"
                    className="mt-6 rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Submit Resume</button>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="careers-jobs-grid">
            {filtered.map((j) => (
              <div key={j.id} className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl" data-testid={`careers-job-${j.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="rounded-full bg-saffron/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-saffron">{j.department}</span>
                  {j.featured && <span className="rounded-full bg-navy px-2.5 py-1 text-[9px] font-bold uppercase text-saffron">Featured</span>}
                </div>
                <h3 className="mt-3 font-heading text-lg font-extrabold text-navy">{j.title}</h3>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-saffron" />{j.location}</span>
                  <span>{j.employment_type}</span>
                  {j.experience && <span>{j.experience}</span>}
                  {j.show_salary && j.salary && <span className="text-emerald-600">{j.salary}</span>}
                </div>
                {j.description && <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-500">{j.description}</p>}
                {!!(j.skills || []).length && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {j.skills.slice(0, 4).map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{s}</span>)}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="font-mono text-[10px] font-bold text-slate-400">{j.job_id}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setOpenJob(j)} data-testid={`careers-view-${j.id}`}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-navy hover:border-saffron hover:text-saffron">View Job</button>
                    <button onClick={() => setApplyJob(j)} data-testid={`careers-apply-${j.id}`}
                            className="rounded-lg bg-saffron px-3 py-1.5 text-[11px] font-bold text-white hover:bg-saffron-hover">Apply Now</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* STATS */}
      {stats.length > 0 && (
        <section className="border-y border-slate-200 bg-white py-12" data-testid="careers-stats">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 sm:px-6 lg:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`careers-stat-${i}`}>
                <p className="font-heading text-3xl font-extrabold text-navy sm:text-4xl">{s.value}</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-saffron">{s.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHY WORK */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="careers-why">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Why Work With IndiaFast</p>
        <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">Grow Where the Action Is</h2>
        <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <div key={i} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl" data-testid={`careers-why-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><w.icon className="h-6 w-6" /></span>
              <h3 className="mt-4 font-heading text-base font-bold text-navy">{w.title}</h3>
              <p className="mt-1.5 text-sm text-slate-500">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CAREER PATH */}
      <section className="bg-white py-16 sm:py-20" data-testid="careers-path">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-saffron">Career Growth</p>
          <h2 className="mt-2 text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Your Growth Can Move in Many Directions</h2>
          <div className="relative mt-12 flex snap-x items-start gap-4 overflow-x-auto pb-3 lg:justify-between lg:overflow-visible">
            <span className="absolute left-6 right-6 top-6 hidden h-0.5 bg-gradient-to-r from-saffron via-slate-200 to-emerald-600 lg:block" />
            {PATH.map((p, i) => (
              <div key={p} className="relative flex w-28 shrink-0 snap-start flex-col items-center text-center lg:w-auto" data-testid={`careers-path-${i}`}>
                <span className={`relative z-10 grid h-12 w-12 place-items-center rounded-full font-heading text-xs font-extrabold ring-4 ring-white ${i === PATH.length - 1 ? "bg-emerald-600 text-white" : "bg-navy text-saffron"}`}>{i + 1}</span>
                <p className="mt-2.5 text-[11px] font-bold text-navy sm:text-xs">{p}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-[11px] text-slate-400">Illustrative growth path — every career journey at IndiaFast is unique.</p>
        </div>
      </section>

      {/* DEPARTMENTS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="careers-departments">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Departments</p>
        <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Find Your Team</h2>
        <div className="mt-9 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {DEPT_CARDS.map((d, i) => (
            <button key={d.name} onClick={() => { setSearch({ ...search, department: d.name === "Warehouse" ? "Warehouse" : d.name }); document.getElementById("careers-jobs")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid={`careers-dept-${i}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><d.icon className="h-5 w-5" /></span>
              <p className="mt-3 font-heading text-sm font-bold text-navy">{d.name}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{d.desc}</p>
              <p className="mt-2 flex items-center gap-1 text-[10px] font-bold text-saffron opacity-0 transition-opacity group-hover:opacity-100">View roles <ArrowRight className="h-3 w-3" /></p>
            </button>
          ))}
        </div>
      </section>

      {/* LIFE AT INDIAFAST */}
      {lifeMedia.length > 0 && (
        <section className="bg-white py-16 sm:py-20" data-testid="careers-life">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Life at IndiaFast</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Moments From Across the Network</h2>
            <div className="mt-8 columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4">
              {lifeMedia.map((m) => (
                <button key={m.id} onClick={() => (m.media_type === "video" || m.media_type === "reel") ? setPlayer(m) : null}
                        data-testid={`careers-life-${m.id}`}
                        className="group relative block w-full break-inside-avoid overflow-hidden rounded-2xl text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl">
                  {m.card_path || m.file_path ? (
                    <img src={`${API_BASE}/api/mediahub/public/file/${m.id}/card`} alt={m.title} loading="lazy"
                         className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                         style={{ objectPosition: `${m.focal_x ?? 50}% ${m.focal_y ?? 50}%` }} />
                  ) : (
                    <div className="grid aspect-square w-full place-items-center bg-navy"><Users className="h-8 w-8 text-white/30" /></div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                    <p className="line-clamp-1 text-xs font-bold text-white">{m.title}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EMPLOYEE STORIES */}
      {stories.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="careers-stories">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Employee Stories</p>
          <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Meet the People Behind IndiaFast</h2>
          <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {stories.map((s, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl" data-testid={`careers-story-${i}`}>
                {s.photo_path && <img src={`${API_BASE}/api/files/${s.photo_path}`} alt={s.name} loading="lazy" className="h-52 w-full object-cover" />}
                <div className="p-5">
                  <p className="font-heading text-base font-bold text-navy">{s.name}</p>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-saffron">{s.designation}{s.department ? ` · ${s.department}` : ""}</p>
                  {s.story && <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-500">{s.story}</p>}
                  <p className="mt-3 text-[10px] font-semibold text-slate-400">{[s.location, s.years && `${s.years} with IndiaFast`].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TEAM VIDEOS / CULTURE */}
      {teamVideos.length > 0 && (
        <section className="bg-white py-16 sm:py-20" data-testid="careers-team-videos">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Hear From Our Team</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Stories in Their Own Words</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {teamVideos.slice(0, 6).map((m) => (
                <button key={m.id} onClick={() => setPlayer(m)} data-testid={`careers-video-${m.id}`}
                        className="group relative overflow-hidden rounded-2xl text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative aspect-video bg-navy">
                    {(m.card_path || m.file_path) && <img src={`${API_BASE}/api/mediahub/public/file/${m.id}/card`} alt={m.title} loading="lazy" className="h-full w-full object-cover" style={{ objectPosition: `${m.focal_x ?? 50}% ${m.focal_y ?? 50}%` }} />}
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-saffron text-white shadow-xl shadow-saffron/40 transition-transform group-hover:scale-110"><Send className="hidden" /><Play2 /></span>
                    </span>
                  </div>
                  <div className="bg-white p-4">
                    <p className="line-clamp-1 font-heading text-sm font-bold text-navy">{m.title}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">{m.creator || "Team IndiaFast"}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CULTURE VIDEO */}
      <section className="relative overflow-hidden bg-navy py-20" data-testid="careers-culture">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        {cms?.culture_video_poster && <img src={`${API_BASE}/api/files/${cms.culture_video_poster}`} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">More Than a Workplace</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300 sm:text-base">The people, the energy, the mission — see what makes IndiaFast move.</p>
          {cms?.culture_video_path && (
            <button onClick={() => setPlayer({ id: "culture", title: "Life at IndiaFast", media_type: "video", file_path: cms.culture_video_path, external_url: cms.culture_video_url || "" })}
                    data-testid="careers-culture-play"
                    className="mx-auto mt-8 grid h-20 w-20 place-items-center rounded-full bg-saffron text-white shadow-2xl shadow-saffron/40 transition-transform hover:scale-105">
              <Play2 big />
            </button>
          )}
        </div>
      </section>

      {/* TALENT COMMUNITY */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="careers-talent">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="tricolor-beam h-1.5" />
          <div className="p-8 text-center sm:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Talent Community</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Don't See the Right Role?</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500">
              Send us your resume. When an opportunity matches your skills, our team can review your profile.
            </p>
            <button onClick={() => setTalentOpen(true)} data-testid="careers-talent-cta"
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-express">
              <Upload className="h-4 w-4" /> SUBMIT YOUR RESUME
            </button>
          </div>
        </div>
      </section>

      {/* VALUES */}
      {values.length > 0 && (
        <section className="bg-white py-16 sm:py-20" data-testid="careers-values">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-saffron">Company Values</p>
            <h2 className="mt-2 text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">What We Stand For</h2>
            <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v, i) => {
                const Icon = VALUE_ICONS[i % 4];
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center transition-all hover:-translate-y-1 hover:bg-white hover:shadow-lg" data-testid={`careers-value-${i}`}>
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-navy text-saffron"><Icon className="h-6 w-6" /></span>
                    <p className="mt-4 font-heading text-sm font-bold text-navy">{v.title}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{v.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20" data-testid="careers-faq">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-saffron">Careers FAQ</p>
          <h2 className="mt-2 text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Questions, Answered</h2>
          <div className="mt-9 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`careers-faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`careers-faq-toggle-${i}`}
                        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50">
                  <span className="text-sm font-bold text-navy">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="border-t border-slate-100 px-5 py-4 text-sm leading-relaxed text-slate-500">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="careers-final-cta">
        {heroImg && <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/50" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Your Next Chapter Could Start Here.</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">Explore opportunities and grow with IndiaFast Global Couriers.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="#careers-jobs" data-testid="careers-final-jobs" className="rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">View Open Positions</a>
            <button onClick={() => setTalentOpen(true)} data-testid="careers-final-resume"
                    className="rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">Submit Resume</button>
          </div>
        </div>
      </section>

      <Footer contact={contact} />

      {/* JOB DETAIL MODAL */}
      {openJob && <JobModal job={openJob} onClose={() => setOpenJob(null)} onApply={() => { setApplyJob(openJob); setOpenJob(null); }} />}

      {/* APPLICATION FORM MODAL */}
      {applyJob && <ApplyModal job={applyJob} maxMb={cms?.resume_max_mb || 5} onClose={() => setApplyJob(null)} onDone={(id) => { setApplyJob(null); setSuccess(id); }} />}

      {/* TALENT FORM MODAL */}
      {talentOpen && <TalentModal maxMb={cms?.resume_max_mb || 5} onClose={() => setTalentOpen(false)} onDone={() => { setTalentOpen(false); setSuccess("TALENT"); }} />}

      {/* SUCCESS */}
      {success && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-navy/80 p-4 backdrop-blur-sm" data-testid="careers-success">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white text-center shadow-2xl">
            <div className="tricolor-beam h-1.5" />
            <div className="p-8">
              <span className="mx-auto grid h-18 w-18 h-20 w-20 place-items-center rounded-full bg-emerald-600/10"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></span>
              <h3 className="mt-5 font-heading text-xl font-extrabold text-navy">
                {success === "TALENT" ? "Profile Submitted Successfully" : "Application Submitted Successfully"}
              </h3>
              <p className="mt-2 text-sm text-slate-500">Thank you for your interest in joining IndiaFast. Our recruitment team will review your {success === "TALENT" ? "profile" : "application"}.</p>
              {success !== "TALENT" && <p className="mt-4 font-mono text-2xl font-extrabold text-express" data-testid="careers-success-id">{success}</p>}
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => { setSuccess(null); document.getElementById("careers-jobs")?.scrollIntoView({ behavior: "smooth" }); }}
                        data-testid="careers-success-jobs" className="rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">Explore More Jobs</button>
                <button onClick={() => setSuccess(null)} data-testid="careers-success-back"
                        className="rounded-full border border-slate-300 px-6 py-2.5 text-sm font-bold text-navy hover:bg-slate-100">Back to Careers</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {player && (player.id === "culture"
        ? <CulturePlayer path={player.file_path} url={player.external_url} onClose={() => setPlayer(null)} />
        : <PlayerModal m={player} onClose={() => setPlayer(null)} />)}
    </div>
  );
}

function Play2({ big }) {
  return (
    <svg className={`${big ? "h-8 w-8" : "h-5 w-5"} ml-0.5`} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  );
}

function CulturePlayer({ path, url, onClose }) {
  const m = { id: "culture", title: "Life at IndiaFast", media_type: "video", file_path: path, external_url: url };
  return <PlayerModal m={m} onClose={onClose} />;
}

function JobModal({ job, onClose, onApply }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  const List = ({ title, items }) => !!items?.length && (
    <div className="mt-5">
      <p className="text-[11px] font-bold uppercase tracking-wider text-saffron">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((r, i) => <li key={i} className="flex gap-2 text-sm text-slate-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{r}</li>)}
      </ul>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="careers-job-modal">
      <div className="mx-auto my-8 w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="tricolor-beam h-1.5" />
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="rounded-full bg-saffron/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-saffron">{job.department}</span>
              <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy" data-testid="careers-job-modal-title">{job.title}</h3>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs font-semibold text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-saffron" />{job.location}</span>
                <span>{job.employment_type}</span>
                {job.experience && <span>{job.experience}</span>}
                {job.show_salary && job.salary && <span className="text-emerald-600">{job.salary}</span>}
                <span className="font-mono text-slate-400">{job.job_id}</span>
              </div>
            </div>
            <button onClick={onClose} data-testid="careers-job-modal-close" aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-saffron hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          {job.description && <p className="mt-5 text-sm leading-relaxed text-slate-600">{job.description}</p>}
          <List title="Key Responsibilities" items={job.responsibilities} />
          <List title="Requirements" items={job.requirements} />
          <List title="Qualifications" items={job.qualifications} />
          {!!(job.skills || []).length && (
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-saffron">Skills</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {job.skills.map((s) => <span key={s} className="rounded-full bg-navy px-3 py-1 text-[11px] font-bold text-saffron">{s}</span>)}
              </div>
            </div>
          )}
          <List title="What We Offer" items={job.benefits} />
          <button onClick={onApply} data-testid="careers-job-modal-apply"
                  className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
            APPLY FOR THIS POSITION <ArrowRight className="h-4 w-4" />
          </button>
          {job.contact_email && <p className="mt-3 text-center text-[11px] text-slate-400">Questions? {job.contact_email}</p>}
        </div>
      </div>
    </div>
  );
}

function ApplyModal({ job, maxMb, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", current_location: "", current_company: "", current_title: "", experience_years: "", current_ctc: "", expected_ctc: "", notice_period: "", qualification: "", skills: "", linkedin: "", portfolio: "", message: "", consent: false });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) { toast.error("Upload your resume (PDF/DOC/DOCX)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      fd.append("resume", resume);
      const r = await api.post("/careers/public/apply", fd);
      onDone(r.data.application_id);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="careers-apply-modal">
      <div className="mx-auto my-8 w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="tricolor-beam h-1.5" />
        <form onSubmit={submit} className="p-6 sm:p-8">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-saffron">Applying for</p>
              <h3 className="mt-1 font-heading text-xl font-extrabold text-navy">{job.title} <span className="font-mono text-sm text-slate-400">· {job.job_id}</span></h3>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-saffron hover:text-white"><X className="h-4 w-4" /></button>
          </div>

          <p className="mb-2 mt-6 text-[11px] font-bold uppercase tracking-wider text-slate-400">Personal Details</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input required className={input} data-testid="apply-name" placeholder="Full Name *" value={form.name} onChange={set("name")} />
            <input required inputMode="tel" className={input} data-testid="apply-phone" placeholder="Mobile Number *" value={form.phone} onChange={set("phone")} />
            <input required type="email" className={input} data-testid="apply-email" placeholder="Email *" value={form.email} onChange={set("email")} />
            <input required className={input} data-testid="apply-city" placeholder="City *" value={form.city} onChange={set("city")} />
            <input required className={`${input} sm:col-span-2`} data-testid="apply-location" placeholder="Current Location *" value={form.current_location} onChange={set("current_location")} />
          </div>

          <p className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Professional Details</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={input} data-testid="apply-company" placeholder="Current Company" value={form.current_company} onChange={set("current_company")} />
            <input className={input} data-testid="apply-title" placeholder="Current Job Title" value={form.current_title} onChange={set("current_title")} />
            <input className={input} data-testid="apply-experience" placeholder="Years of Experience" value={form.experience_years} onChange={set("experience_years")} />
            <input className={input} data-testid="apply-qualification" placeholder="Highest Qualification" value={form.qualification} onChange={set("qualification")} />
            <input className={input} data-testid="apply-current-ctc" placeholder="Current CTC" value={form.current_ctc} onChange={set("current_ctc")} />
            <input className={input} data-testid="apply-expected-ctc" placeholder="Expected CTC" value={form.expected_ctc} onChange={set("expected_ctc")} />
            <input className={input} data-testid="apply-notice" placeholder="Notice Period" value={form.notice_period} onChange={set("notice_period")} />
            <input className={input} data-testid="apply-skills" placeholder="Skills (comma separated)" value={form.skills} onChange={set("skills")} />
          </div>

          <p className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Resume *</p>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-xs font-bold text-slate-500 transition-colors hover:border-saffron hover:text-saffron" data-testid="apply-resume">
            <Upload className="h-4 w-4" />
            {resume ? `${resume.name} ✓` : `Upload Resume (PDF / DOC / DOCX, max ${maxMb} MB)`}
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0] || null)} />
          </label>

          <p className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Additional Information</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="url" className={input} data-testid="apply-linkedin" placeholder="LinkedIn URL" value={form.linkedin} onChange={set("linkedin")} />
            <input type="url" className={input} data-testid="apply-portfolio" placeholder="Portfolio URL" value={form.portfolio} onChange={set("portfolio")} />
            <textarea rows={2} className={`${input} sm:col-span-2`} data-testid="apply-message" placeholder="Message to the recruitment team" value={form.message} onChange={set("message")} />
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-2.5 text-xs text-slate-600" data-testid="apply-consent">
            <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-saffron" />
            I confirm that the information provided is accurate and I agree to be contacted by IndiaFast regarding employment opportunities.
          </label>

          <button disabled={busy} data-testid="apply-submit"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} SUBMIT APPLICATION
          </button>
        </form>
      </div>
    </div>
  );
}

function TalentModal({ maxMb, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", department: "", location: "", experience: "", skills: "", message: "", consent: false });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (resume) fd.append("resume", resume);
      await api.post("/careers/public/talent", fd);
      toast.success("Profile added to the Talent Community");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="talent-modal">
      <div className="mx-auto my-8 w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="tricolor-beam h-1.5" />
        <form onSubmit={submit} className="p-6 sm:p-8">
          <div className="flex items-start justify-between">
            <h3 className="font-heading text-xl font-extrabold text-navy">Join the Talent Community</h3>
            <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-saffron hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <input required className={input} data-testid="talent-name" placeholder="Full Name *" value={form.name} onChange={set("name")} />
            <input required type="email" className={input} data-testid="talent-email" placeholder="Email *" value={form.email} onChange={set("email")} />
            <input required inputMode="tel" className={input} data-testid="talent-phone" placeholder="Mobile *" value={form.phone} onChange={set("phone")} />
            <select className={input} data-testid="talent-dept" value={form.department} onChange={set("department")}>
              <option value="">Preferred Department</option>{DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <input className={input} data-testid="talent-location" placeholder="Preferred Location" value={form.location} onChange={set("location")} />
            <input className={input} data-testid="talent-experience" placeholder="Experience (e.g. 1–3 Years)" value={form.experience} onChange={set("experience")} />
            <input className={`${input} sm:col-span-2`} data-testid="talent-skills" placeholder="Skills (comma separated)" value={form.skills} onChange={set("skills")} />
            <textarea rows={2} className={`${input} sm:col-span-2`} data-testid="talent-message" placeholder="Message" value={form.message} onChange={set("message")} />
          </div>
          <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-xs font-bold text-slate-500 hover:border-saffron hover:text-saffron" data-testid="talent-resume">
            <Upload className="h-4 w-4" /> {resume ? `${resume.name} ✓` : `Resume (optional, PDF/DOC/DOCX max ${maxMb} MB)`}
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0] || null)} />
          </label>
          <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-xs text-slate-600" data-testid="talent-consent">
            <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-saffron" />
            I agree to be contacted by IndiaFast regarding employment opportunities.
          </label>
          <button disabled={busy} data-testid="talent-submit"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-sm font-bold text-white transition-colors hover:bg-express disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} SUBMIT
          </button>
        </form>
      </div>
    </div>
  );
}
